import { CommonModule } from '@angular/common';
import { Component, inject, input, output, signal, type InputSignal, type OutputEmitterRef, type SimpleChanges, type WritableSignal } from '@angular/core';
import { TableModule } from 'primeng/table';
import { PaymentService } from '../../../services/payment.service';
import { Payment, PaymentStatus } from '../../../interfaces/payment';
import { Order, TableColumn, type PageControl } from '../../../interfaces/application';
import { PaymentStatusPipe } from "../../pipes/payment-status-pipe";
import { TooltipModule } from 'primeng/tooltip';
import { PaginatorModule, type PaginatorState } from 'primeng/paginator';
import { finalize } from 'rxjs';
import dayjs from 'dayjs';
import { LottieComponent, type AnimationOptions } from 'ngx-lottie';

@Component({
  selector: 'app-table-payments',
  imports: [
    CommonModule,
    TableModule,
    TooltipModule,
    PaymentStatusPipe,
    PaginatorModule,
    LottieComponent
],
  templateUrl: './table-payments.html',
  styleUrl: './table-payments.css'
})
export class TablePayments {

  public emptyLottieOptions: AnimationOptions = {
    path: 'assets/lottie/empty.json',
    loop: true,
    autoplay: true,
  };

  private readonly _paymentService: PaymentService = inject(PaymentService);

  public localLoading: WritableSignal<boolean> = signal<boolean>(false);
  public loading: InputSignal<boolean> = input<boolean>(false);

  public search_term: InputSignal<string> = input<string>('');
  public date_range: InputSignal<string> = input<string>('');
  public date_field: InputSignal<string> = input<string>('');
  public category_id: InputSignal<string> = input<string>('');
  public people_id: InputSignal<string> = input<string>('');
  public status: InputSignal<string> = input<string>('');

  public edit: OutputEmitterRef<Payment> = output<Payment>();

  public extraInfo: any = null;
  public items: Payment[] = [];

  public columns: TableColumn[] = [
    {
      label: 'Data da Solicitação',
      slug: 'created_at',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Documento',
      slug: 'nfse_key',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Valor',
      slug: 'value',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Fornecedor/Prestador',
      slug: 'receiver',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Status',
      slug: 'status',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Categoria',
      slug: 'category',
      align: 'text-start',
      sortable: false,
    },
    {
      label: 'Data do Vencimento',
      slug: 'due_date',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Data do Pagamento',
      slug: 'paid_at',
      align: 'text-start',
      sortable: true,
    },
    {
      label: 'Ações',
      slug: '',
      align: 'text-start',
      sortable: false,
    }
  ];

  public pageControl: PageControl = {
    page: 0,
    take: 10,
    itemCount: 0,
    pageCount: 0,
    orderField: 'created_at ',
    order: Order.DESC
  }

  protected readonly paymentStatus: typeof PaymentStatus = PaymentStatus;
  protected readonly order: typeof Order = Order;

  public ngOnChanges(changes: SimpleChanges): void {
    const {
      loading,
      search_term,
      date_range,
      date_field,
      category_id,
      people_id,
      status,
    } = changes;

    if (!loading?.currentValue) {
      this.loadData();
    } else if (search_term?.currentValue && search_term?.currentValue !== search_term?.previousValue) {
      this.loadData();
    } else if (date_range?.currentValue && date_range?.currentValue !== date_range?.previousValue) {
      this.loadData();
    } else if (date_range?.currentValue && date_range?.currentValue !== date_range?.previousValue) {
      this.loadData();
    } else if (date_field?.currentValue && date_field?.currentValue !== date_field?.previousValue) {
      this.loadData();
    } else if (people_id?.currentValue && people_id?.currentValue !== people_id?.previousValue) {
      this.loadData();
    } else if (status?.currentValue && status?.currentValue !== status?.previousValue) {
      this.loadData();
    }
  }

  public loadData(): void {

    const filters = {
      search_term: this.search_term() || '',
      start_date: this.date_range()[0] ? dayjs(this.date_range()[0]).format('YYYY-MM-DD') : '',
      end_date: this.date_range()[1] ? dayjs(this.date_range()[1]).format('YYYY-MM-DD') : '',
      date_field: this.date_field() || '',
      category_id: this.category_id() || '',
      people_id: this.people_id() || '',
      status: this.status() || '',
    };

    this.localLoading.set(true);
    this._paymentService.get(this.pageControl, filters)
      .pipe(finalize(() => this.localLoading.set(false)))
      .subscribe({
        next: response => {
          this.items = response.data;
          this.extraInfo = response.extra;

          this.pageControl.page = response.meta.current_page;
          this.pageControl.itemCount = response.meta.total;
        },
        error: () => {
          this.items = [];
          this.extraInfo = null;
          this.pageControl.page = 0;
          this.pageControl.itemCount = 0;
        }
      })
  }

  public sort(slug: string): void {
    this.pageControl.page = 0;
    this.pageControl.orderField = slug;
    this.pageControl.order = this.pageControl.order === Order.ASC ? Order.DESC : Order.ASC;

    this.loadData();
  }

  public onPageChange(e: PaginatorState): void {
    this.pageControl.page = (e?.page || 0) + 1;
    this.pageControl.take = (e?.rows || 10);

    this.loadData();
  }

  public openOutsideUrl(url: string): void {
    window.open(url, '_blank');
  }
}
